import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

export interface PremiumComboboxOption {
  value: string;
  label: string;
  /** Optional secondary text shown under the label in the list (not the trigger). */
  sublabel?: string;
}

interface PremiumComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  options: PremiumComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  icon?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
}

/**
 * A searchable, custom-styled replacement for the plain shadcn <Select> — used
 * where a dropdown has enough options (or enough visual weight) to earn a
 * nicer treatment: gradient trigger, search-as-you-type, checkmarked selection.
 * Built on the existing Popover + Command (cmdk) primitives, so it inherits
 * their keyboard nav and theming for free.
 */
export function PremiumCombobox({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
  icon,
  className,
  triggerClassName,
}: PremiumComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find(o => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'group flex h-9 items-center gap-2 rounded-lg border border-border/70 bg-gradient-to-b from-background to-muted/40 px-3 text-sm shadow-sm transition-all',
            'hover:border-primary/40 hover:shadow-md',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
            'data-[state=open]:border-primary/50 data-[state=open]:ring-2 data-[state=open]:ring-primary/20',
            className,
            triggerClassName,
          )}
        >
          {icon}
          <span className={cn('flex-1 truncate text-left font-medium', selected ? 'text-foreground' : 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] overflow-hidden rounded-xl border-border/70 p-0 shadow-xl"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-10" />
          <CommandList>
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map(opt => (
                <CommandItem
                  key={opt.value}
                  value={`${opt.label} ${opt.sublabel ?? ''}`}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                  className="gap-2 rounded-lg py-2 data-[selected=true]:bg-primary/10 data-[selected=true]:text-foreground"
                >
                  <Check className={cn('h-4 w-4 shrink-0 text-primary', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
